import { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../../Config/axiosInstance";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

import Select from "react-select";
import makeAnimated from "react-select/animated";

import "./MatrixDialog.css";

// activăm animațiile react-select
const animatedComponents = makeAnimated();

// scoate id-ul departmentului 
const getDeptId = (dept) => {
  if (!dept) return null;

  if (typeof dept === "number") return dept;

  if (typeof dept === "string") {
    const n = Number(dept);
    return Number.isFinite(n) ? n : null;
  }

  if (typeof dept === "object") return dept.id ?? dept.value ?? null;

  return null;
};

export default function CodeMatrixEditDialog({
  open,
  onClose,
  departments,
  row,
  onUpdated,
}) {

  // STATE – VALORI FORMULAR
  const [department, setDepartment] = useState(null);
  const [activity, setActivity] = useState("");
  const [activityAbbr, setActivityAbbr] = useState("");

  // loading la submit
  const [saving, setSaving] = useState(false);

  // STATE – ERORI PE CÂMP
  const [errors, setErrors] = useState({
    department: "",
    activity: "",
    activityAbbr: "",
    submit: "",
  });

  // state submit
  const [submitted, setSubmitted] = useState(false);

  // OPTIONS PENTRU DEPARTMENT
  const departmentOptions = useMemo(
    () =>
      (departments || []).map((d) => ({
        value: d.id,
        label: `${d.name}${d.department_abbreviated ? ` (${d.department_abbreviated})` : ""}`,
      })),
    [departments]
  );

  // RESET + PREFILL FORM (când se deschide)
  useEffect(() => {
    if (!open) return;

    setSaving(false);
    setSubmitted(false);

    // reset erori
    setErrors({
      department: "",
      activity: "",
      activityAbbr: "",
      submit: "",
    });

    // dacă row e null, golim
    if (!row) {
      setDepartment(null);
      setActivity("");
      setActivityAbbr("");
      return;
    }

    // prefill text
    setActivity(row.activity ?? "");
    setActivityAbbr(row.activity_abbreviated ?? "");

    // setăm department după ce avem options 
    setDepartment(null);
  }, [open, row]);

  // PREFILL department 
  useEffect(() => {
    if (!open) return;
    if (!row) return;

    const deptId = getDeptId(row.department_id ?? row.department);
    if (!deptId) return;

    const opt = departmentOptions.find((o) => o.value === deptId) || null;
    setDepartment(opt);
  }, [open, row, departmentOptions]);

  // VALIDARE GLOBALĂ (la Submit)
  const validateAll = () => {
    const next = {
      department: "",
      activity: "",
      activityAbbr: "",
      submit: "",
    };

    if (!department) next.department = "Required";
    if (!activity.trim()) next.activity = "Required";
    if (!activityAbbr.trim()) next.activityAbbr = "Required";

    setErrors((prev) => ({ ...prev, ...next }));

    return !next.department && !next.activity && !next.activityAbbr;
  };

  // SUBMIT FORM (EDIT)
  const handleSubmit = async () => {
    setSubmitted(true);
    setErrors((prev) => ({ ...prev, submit: "" }));

    // validare înainte de request
    if (!validateAll()) return;

    if (!row?.id) {
      setErrors((prev) => ({ ...prev, submit: "Missing row id." }));
      return;
    }

    setSaving(true);

    try {
      await axiosInstance.patch(`/code_matrix/${row.id}/`, {
        department: department.value,
        activity: activity.trim(),
        activity_abbreviated: activityAbbr.trim(),
      });

      // refresh list + închide dialog
      onUpdated?.();
      onClose?.();
    } catch (e) {

      // eroare backend afișată jos
      setErrors((prev) => ({
        ...prev,
        submit: e.response?.data ? JSON.stringify(e.response.data) : e.message,
      }));
    } finally {
      setSaving(false);
    }
  };

  // FLAG-URI PENTRU AFIȘARE ERORI
  const showDeptError = submitted && !!errors.department;
  const showActivityError = submitted && !!errors.activity;
  const showAbbrError = submitted && !!errors.activityAbbr;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      className="code-matrix-dialog"
    >
      {/* TITLU */}
      <DialogTitle className="cmd-title">
        Edit Code Matrix
      </DialogTitle>

      {/* CONȚINUT */}
      <DialogContent dividers className="cmd-content">
        {/* DEPARTMENT */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Department <span style={{ color: "red" }}>*</span>
          </label>

          <Select
            options={departmentOptions}
            value={department}
            // setam valoarea si stergem eroarea instant
            onChange={(val) => {
              setDepartment(val);
              if (val) {
                setErrors((prev) => ({ ...prev, department: "" }));
              }
            }}
            onBlur={() => {
              if (!submitted) return;
              setErrors((prev) => ({
                ...prev,
                department: department ? "" : "Required",
              }));
            }}
            isClearable
            isSearchable
            placeholder="Select department"
            classNamePrefix="cmd-react-select"
            components={animatedComponents}
            isDisabled={saving}
            // fix z-index pentru Dialog
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 20000 }),
              menu: (base) => ({ ...base, zIndex: 20000 }),
            }}
            className={showDeptError ? "cmd-select-error" : ""}
          />

          {showDeptError && (
            <div className="cmd-field-error">{errors.department}</div>
          )}
        </div>

        {/* ACTIVITY */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Activity <span style={{ color: "red" }}>*</span>
          </label>

          <input
            type="text"
            className={`cmd-input ${showActivityError ? "cmd-input-error" : ""}`}
            value={activity}
            // sterge eroarea imediat ce userul scrie
            onChange={(e) => {
              const val = e.target.value;
              setActivity(val);
              if (val.trim()) {
                setErrors((prev) => ({ ...prev, activity: "" }));
              }
            }}
            // după submit, validează
            onBlur={() => {
              if (!submitted) return;
              setErrors((prev) => ({
                ...prev,
                activity: activity.trim() ? "" : "Required",
              }));
            }}
            disabled={saving}
          />

          {showActivityError && (
            <div className="cmd-field-error">{errors.activity}</div>
          )}
        </div>

        {/* ACTIVITY ABBREVIATION */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Activity Abbreviation <span style={{ color: "red" }}>*</span>
          </label>

          <input
            type="text"
            className={`cmd-input ${showAbbrError ? "cmd-input-error" : ""}`}
            value={activityAbbr}
            // sterge eroarea instant
            onChange={(e) => {
              const val = e.target.value;
              setActivityAbbr(val);
              if (val.trim()) {
                setErrors((prev) => ({ ...prev, activityAbbr: "" }));
              }
            }}
            // revalideaza la blur
            onBlur={() => {
              if (!submitted) return;
              setErrors((prev) => ({
                ...prev,
                activityAbbr: activityAbbr.trim() ? "" : "Required",
              }));
            }}
            disabled={saving}
          />

          {showAbbrError && (
            <div className="cmd-field-error">{errors.activityAbbr}</div>
          )}
        </div>

        {/* EROARE DE SUBMIT */}
        {errors.submit && <div className="cmd-submit-error">{errors.submit}</div>}
      </DialogContent>

      {/* Butoane */}
      <DialogActions className="cmd-actions">
        <Button
          onClick={onClose}
          disabled={saving}
          className="cmd-btn cmd-btn-cancel"
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={saving || !row?.id}
          variant="contained"
          className="cmd-btn cmd-btn-create"
          startIcon={
            saving ? <CircularProgress size={18} className="cmd-spinner" /> : null
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
